import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { allDemoConversations } from "../../data/demoMessages";
import { Conversation } from "../../types/messages";
import { formatDistanceToNow } from "date-fns";

interface MessagesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MessagesModal({ isOpen, onClose }: MessagesModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  // Use our structured demo conversations
  const conversations = allDemoConversations;

  // Helper function to get the other participant in a conversation
  const getOtherParticipant = (conversation: Conversation) => {
    if (conversation.is_group) {
      return {
        name: conversation.group_name || 'Group Chat',
        avatar: conversation.group_avatar || 'https://images.unsplash.com/photo-1522071820081-009f0129c71c',
      };
    }

    const otherParticipant = conversation.participants.find(p => p.user_id !== 'current-user');
    return {
      name: otherParticipant?.full_name || otherParticipant?.username || 'Unknown User',
      avatar: otherParticipant?.avatar_url || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde',
      online: otherParticipant?.online || false,
    };
  };

  const handleMessageClick = (conversationId: string) => {
    navigate("/messages", { state: { selectedConversationId: conversationId } });
    setTimeout(() => {
      onClose();
    }, 0);
  };

  const handleMessagesClick = () => {
    navigate("/messages");
    setTimeout(() => {
      onClose();
    }, 0);
  };

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      <div className="absolute bottom-0 left-0 w-64 bg-white dark:bg-black dark:shadow-[6px_-5px_6px_-1px_rgba(149,_157,_165,_0.2)] rounded-tr-xl h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <button
              onClick={handleMessagesClick}
              className="text-lg font-semibold dark:text-white hover:text-gray-600 dark:hover:text-gray-300"
            >
              Messages
            </button>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {conversations.length} conversations
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          <div className="h-full">
            {conversations.map((conversation) => {
              const otherParticipant = getOtherParticipant(conversation);
              const lastMessage = conversation.last_message;
              const hasUnread = lastMessage && !lastMessage.read && lastMessage.sender_id !== 'current-user';

              // Format the time (e.g., "5m", "2h", "1d")
              const timeAgo = lastMessage ?
                formatDistanceToNow(new Date(lastMessage.timestamp), { addSuffix: false })
                  .replace(' minutes', 'm')
                  .replace(' minute', 'm')
                  .replace(' hours', 'h')
                  .replace(' hour', 'h')
                  .replace(' days', 'd')
                  .replace(' day', 'd')
                : '';

              return (
                <button
                  key={conversation.id}
                  onClick={() => handleMessageClick(conversation.id)}
                  className="flex items-center space-x-3 w-full p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-b border-gray-100 dark:border-gray-800"
                >
                  <div className="relative">
                    <img
                      src={otherParticipant.avatar}
                      alt={otherParticipant.name}
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                    {otherParticipant.online && (
                      <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full ring-2 ring-white dark:ring-gray-900" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p
                        className={`font-medium truncate ${hasUnread
                            ? "text-gray-900 dark:text-white"
                            : "text-gray-600 dark:text-gray-400"
                          }`}
                      >
                        {otherParticipant.name}
                      </p>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {timeAgo}
                      </span>
                    </div>
                    <p
                      className={`text-sm truncate ${hasUnread
                          ? "text-gray-800 dark:text-gray-200"
                          : "text-gray-500 dark:text-gray-400"
                        }`}
                    >
                      {lastMessage?.content || 'No messages yet'}
                    </p>
                  </div>
                  {hasUnread && (
                    <div className="w-2.5 h-2.5 bg-brand-green rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
